import type { SignageDeviceRecord } from 'noddi-async';

import type { NoddiColumnDef } from 'noddi-ui-web';
import { NoddiBadge } from 'noddi-ui-web';
import {
  signageContentLabel,
  signageDeviceDisplayName,
  signageDisplayResolution,
  signageFormatDateTime
} from './signageDeviceDisplay';
import { signageFormatMib } from './signageDeviceTelemetry';
import { SignageOnlineStatusBadge } from './SignageOnlineStatusBadge';

type Options = {
  /** Pairing code column (admin fleet). */
  includePairingCode?: boolean;
  /** Service department column (admin multi-tenant fleet). */
  includeDepartment?: boolean;
  /** Chromium crash count (admin diagnostics). */
  includeCrashes?: boolean;
};

type Column = NoddiColumnDef<SignageDeviceRecord>;

const pairingCodeColumn: Column = {
  header: 'Code',
  accessorKey: 'pairingCode'
};

const departmentColumn: Column = {
  header: 'Department',
  accessorFn: (row) => row.serviceDepartmentName ?? null
};

const crashesColumn: Column = {
  header: 'Crashes',
  accessorFn: (row) => row.chromiumCrashCount ?? null
};

/**
 * Shared fleet table columns for partner and admin device lists.
 */
const getSignageFleetColumns = ({
  includePairingCode = false,
  includeDepartment = false,
  includeCrashes = false
}: Options = {}): Column[] => [
  {
    header: 'Name',
    accessorKey: 'name',
    cell: ({ row }) => signageDeviceDisplayName(row.original)
  },
  ...(includePairingCode ? [pairingCodeColumn] : []),
  {
    header: 'Status',
    meta: {
      description:
        'Online status from last seen / heartbeat. Never seen = paired or enrolled during flash, but the device has never booted or checked in.'
    },
    cell: ({ row }) => (
      <div className='flex flex-wrap gap-2'>
        <SignageOnlineStatusBadge status={row.original.onlineStatus} />
        <NoddiBadge variant={row.original.isPaired ? 'success' : 'outline'}>
          {row.original.isPaired ? 'Paired' : 'Unpaired'}
        </NoddiBadge>
        {row.original.pendingCommandCount > 0 && (
          <NoddiBadge variant='info'>{row.original.pendingCommandCount} queued</NoddiBadge>
        )}
      </div>
    )
  },
  {
    header: 'Content',
    accessorFn: (row) => signageContentLabel(row)
  },
  ...(includeDepartment ? [departmentColumn] : []),
  {
    header: 'Building',
    accessorFn: (row) => row.garageName ?? null
  },
  {
    header: 'Host / fleet id',
    accessorFn: (row) => row.hostname || row.fleetDeviceId || null,
    cell: ({ row }) => (
      <span className='font-mono text-xs'>{row.original.hostname || row.original.fleetDeviceId || null}</span>
    )
  },
  {
    header: 'Display',
    accessorFn: (row) => signageDisplayResolution(row)
  },
  {
    header: 'Image',
    accessorFn: (row) => row.imageVersion || null
  },
  {
    header: 'Player',
    accessorFn: (row) => row.playerVersion || null
  },
  {
    header: 'Disk free',
    accessorFn: (row) => (row.diskFreeMb != null ? signageFormatMib(row.diskFreeMb) : null)
  },
  ...(includeCrashes ? [crashesColumn] : []),
  {
    header: 'Last seen',
    accessorFn: (row) => signageFormatDateTime(row.lastSeenAt)
  },
  {
    header: 'Last heartbeat',
    accessorFn: (row) => signageFormatDateTime(row.lastHeartbeatAt)
  }
];

/** Partner fleet default columns. */
export const signageFleetColumns = getSignageFleetColumns();

/** Admin fleet columns with multi-tenant diagnostics. */
export const signageAdminFleetColumns = getSignageFleetColumns({
  includePairingCode: true,
  includeDepartment: true,
  includeCrashes: true
});
